import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "adultsValue", "childrenValue", "adultsInput", "childrenInput",
    "tabBtn", "infoPanel", "tabPanel",
    "regionalPassSelect", "regionalDaysSelect", "regionalClassSelect",
    "fromInput", "toInput", "fromId", "toId",
    "fromSuggestions", "toSuggestions", "dateInput",
  ];

  static values = { searchOptionsUrl: String };

  connect() {
    this.adults = 1;
    this.children = 0;
    this.#syncActiveHeroTab();
  }

  increment(e) {
    const type = e.currentTarget.dataset.heroFormPassengerTypeParam;
    if (type === "children") {
      this.children = Math.min(this.children + 1, 30);
    } else {
      this.adults = Math.min(this.adults + 1, 30);
    }
    this.#updatePassengerDisplay();
  }

  decrement(e) {
    const type = e.currentTarget.dataset.heroFormPassengerTypeParam;
    if (type === "children") {
      this.children = Math.max(this.children - 1, 0);
    } else {
      this.adults = Math.max(this.adults - 1, 1);
    }
    this.#updatePassengerDisplay();
  }

  #updatePassengerDisplay() {
    this.adultsValueTarget.textContent = this.adults;
    this.childrenValueTarget.textContent = this.children;
    this.adultsInputTarget.value = this.adults;
    this.childrenInputTarget.value = this.children;
  }

  switchTab(e) {
    const tab = e.currentTarget.dataset.tab;
    window.heroActiveTab = e.currentTarget.dataset.heroTab;

    this.tabBtnTargets.forEach(btn => {
      const isActive = btn.dataset.tab === tab;
      btn.classList.toggle("homepage-hero__tab--active", isActive && tab === "jrpass");
      btn.classList.toggle("homepage-hero__tab--train-active", isActive && tab === "train");
      btn.classList.toggle("homepage-hero__tab--regional-active", isActive && tab === "regional");
    });

    this.infoPanelTargets.forEach(panel => {
      panel.hidden = panel.dataset.tab !== tab;
    });

    this.tabPanelTargets.forEach(panel => {
      panel.hidden = panel.dataset.tab !== tab;
    });

    if (tab === "train") this.#loadTrainOptions();
  }

  onFromInput() {
    this.fromIdTarget.value = "";
    this.#renderSuggestions("from", this.fromInputTarget, this.fromSuggestionsTarget, this.fromIdTarget);
  }

  onToInput() {
    this.toIdTarget.value = "";
    this.#renderSuggestions("to", this.toInputTarget, this.toSuggestionsTarget, this.toIdTarget);
  }

  onFromFocus() {
    this.#renderSuggestions("from", this.fromInputTarget, this.fromSuggestionsTarget, this.fromIdTarget, { browseAll: true });
  }

  onToFocus() {
    this.#renderSuggestions("to", this.toInputTarget, this.toSuggestionsTarget, this.toIdTarget, { browseAll: true });
  }

  onFromBlur() {
    this.#closeList(this.fromInputTarget, this.fromSuggestionsTarget);
  }

  onToBlur() {
    this.#closeList(this.toInputTarget, this.toSuggestionsTarget);
  }

  onFromKeydown(e) {
    this.#handleKeydown(e, this.fromInputTarget, this.fromSuggestionsTarget, this.fromIdTarget);
  }

  onToKeydown(e) {
    this.#handleKeydown(e, this.toInputTarget, this.toSuggestionsTarget, this.toIdTarget);
  }

  swap(e) {
    e.preventDefault();
    [this.fromInputTarget.value, this.toInputTarget.value] = [this.toInputTarget.value, this.fromInputTarget.value];
    [this.fromIdTarget.value, this.toIdTarget.value] = [this.toIdTarget.value, this.fromIdTarget.value];
  }

  submitTrain(e) {
    e.preventDefault();
    if (!this.search) return;

    const fromId = this.fromIdTarget.value;
    const toId = this.toIdTarget.value;
    const date = this.dateInputTarget.value;

    window.location.href = `${this.search.url}?${this.#buildQuery(fromId, toId, date)}`;
  }

  #buildQuery(fromId, toId, date) {
    const values = {
      from: this.fromInputTarget.value,
      to: this.toInputTarget.value,
      from_id: fromId,
      to_id: toId,
      date,
    };

    return Object.entries(this.search.params)
      .filter(([field]) => values[field] !== undefined)
      .map(([field, name]) => `${name}=${encodeURIComponent(values[field])}`)
      .join("&");
  }

  #loadTrainOptions() {
    if (this.trainLoadPromise) return this.trainLoadPromise;

    this.trainLoadPromise = fetch(this.searchOptionsUrlValue, { headers: { Accept: "application/json" } })
      .then(response => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then(data => this.#applyTrainOptions(data))
      .catch(() => { this.trainLoadPromise = null; });

    return this.trainLoadPromise;
  }

  #applyTrainOptions(data) {
    this.stations = data.stations || [];
    this.destinationIdsByOrigin = data.destination_ids_by_origin || {};
    this.search = data.search;
    this.#applyBookingWindow(data.booking_window || {});
  }

  #applyBookingWindow(bookingWindow) {
    this.minDate = bookingWindow.min_date;
    this.maxDate = bookingWindow.max_date;
    this.#applyDateBounds();
  }

  #applyDateBounds() {
    if (!this.hasDateInputTarget) return;
    if (this.minDate) this.dateInputTarget.min = this.minDate;
    if (this.maxDate) this.dateInputTarget.max = this.maxDate;
    if (!this.dateInputTarget.value && this.minDate) this.dateInputTarget.value = this.minDate;
  }

  #renderSuggestions(field, input, list, hiddenId, { browseAll = false } = {}) {
    list.innerHTML = "";
    list.dataset.activeIndex = "-1";
    input.removeAttribute("aria-activedescendant");

    const matches = browseAll ? this.#availableStations(field) : this.#matchingStations(field, input.value);
    if (matches.length === 0) {
      this.#closeList(input, list);
      return;
    }

    matches.forEach((station, index) => {
      list.appendChild(this.#buildOption(station, index, input, list, hiddenId));
    });
    this.#openList(input, list);
  }

  #availableStations(field) {
    return (this.stations || []).filter(station => {
      const originId = field === "from" ? station.id : this.fromIdTarget.value;
      const destinationId = field === "to" ? station.id : this.toIdTarget.value;

      return this.#routeAvailable(originId, destinationId);
    });
  }

  #matchingStations(field, value) {
    const query = value.trim().toLowerCase();
    const availableStations = this.#availableStations(field);
    if (!query) return availableStations;
    return availableStations.filter(station => this.#stationMatches(station, query)).slice(0, 8);
  }

  #buildOption(station, index, input, list, hiddenId) {
    const item = document.createElement("li");
    item.className = "homepage-hero__train-suggestion";
    item.id = `${list.id}-opt-${index}`;
    item.dataset.stationId = station.id;
    item.dataset.stationName = station.name;
    item.setAttribute("role", "option");
    item.setAttribute("aria-selected", "false");
    item.textContent = station.name;
    item.addEventListener("mousedown", event => {
      event.preventDefault();
      this.#selectOption(item, input, list, hiddenId);
    });
    return item;
  }

  #stationMatches(station, query) {
    if (station.name && station.name.toLowerCase().includes(query)) return true;
    return (station.aliases || []).some(alias => alias.toLowerCase().includes(query));
  }

  #routeAvailable(originId, destinationId) {
    if (!originId || !destinationId) return true;

    return (this.destinationIdsByOrigin[String(originId)] || [])
      .some(id => String(id) === String(destinationId));
  }

  #selectOption(option, input, list, hiddenId) {
    input.value = option.dataset.stationName;
    hiddenId.value = option.dataset.stationId;
    this.#closeList(input, list);
  }

  #openList(input, list) {
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
  }

  #closeList(input, list) {
    list.hidden = true;
    list.innerHTML = "";
    list.dataset.activeIndex = "-1";
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
  }

  #handleKeydown(e, input, list, hiddenId) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.#moveActive(input, list, 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.#moveActive(input, list, -1);
    } else if (e.key === "Enter") {
      this.#selectActive(e, input, list, hiddenId);
    } else if (e.key === "Escape") {
      this.#closeList(input, list);
    }
  }

  #selectActive(e, input, list, hiddenId) {
    const option = this.#activeOption(list);
    if (!option) return;
    e.preventDefault();
    this.#selectOption(option, input, list, hiddenId);
  }

  #activeOption(list) {
    const index = parseInt(list.dataset.activeIndex, 10);
    if (Number.isNaN(index) || index < 0) return null;
    return list.children[index] || null;
  }

  #moveActive(input, list, delta) {
    const count = list.children.length;
    if (count === 0) return;
    const current = parseInt(list.dataset.activeIndex, 10);
    const base = Number.isNaN(current) ? -1 : current;
    this.#setActive(input, list, (base + delta + count) % count);
  }

  #setActive(input, list, index) {
    Array.from(list.children).forEach((option, i) => {
      const selected = i === index;
      option.classList.toggle("homepage-hero__train-suggestion--active", selected);
      option.setAttribute("aria-selected", selected ? "true" : "false");
    });

    list.dataset.activeIndex = String(index);
    const active = list.children[index];
    if (active) {
      input.setAttribute("aria-activedescendant", active.id);
      active.scrollIntoView({ block: "nearest" });
    }
  }

  #syncActiveHeroTab() {
    const activeBtn = this.tabBtnTargets.find(btn => btn.classList.contains("homepage-hero__tab--active")) || this.tabBtnTargets[0];
    if (activeBtn) window.heroActiveTab = activeBtn.dataset.heroTab;
  }

  goToRegional(e) {
    e.preventDefault();
    if (!this.hasRegionalPassSelectTarget) return;
    const pass = this.regionalPassSelectTarget.value;
    const days = this.hasRegionalDaysSelectTarget ? this.regionalDaysSelectTarget.value : '';
    const cls  = this.hasRegionalClassSelectTarget ? this.regionalClassSelectTarget.value : '';
    if (pass) window.location.href = `/regional-passes/${pass}?days=${days}&class=${cls}`;
  }
}
